import {
  Anchor,
  Badge,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core'
import type { Icon } from '@tabler/icons-react'
import { IconAlertTriangle, IconCheck, IconEye, IconGift, IconUserCheck, IconUserPlus } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'

import { useGlobalState } from '@/hooks/useGlobalState'
import { getTrialsEnabled } from '@/screens/AccessScreen/accessSettings'
import { getRoutePath } from '@/utils/navigation'
import { formatDollars, getSeatPrice } from '@/utils/planUtils'
import { DEFAULT_GUEST_MESSAGE_LIMIT } from '@/utils/sponsors'

// Copy approved for SME-1119 (pricing-section.docx, 2026-10-08).
const copy = {
  firstMonth: {
    label: 'Channel-wide',
    title: 'First month free',
    body: 'No charge for any user on your channel during the first month after channel creation.',
    note: 'Add a payment method before your free month ends to keep your channel active. Without a valid payment method, only administrators will be able to access the channel.',
  },
  paidUsers: {
    label: 'Your channel pays',
    title: 'Paid users',
    unit: 'per user per month',
    included: 'Unlimited messages',
    includedNote: '(subject to fair-use policy per the Customer Terms of Service)',
    body: 'Full channel access. No minimum number of users.',
    link: 'Invite users →',
  },
  optionalFeatures: 'Optional features',
  noCharge: 'No charge to your channel',
  userTrials: {
    title: 'User trials',
    body: [
      'Give new users one month of free access to your channel. Their trial begins when they first log in.',
      'When the trial ends, users retain guest access. They can subscribe through your external link or receive full access directly from you.',
    ],
    linkWhenOff: 'Set up user trials →',
    linkWhenOn: 'User trials are on · change them →',
  },
  guestAccess: (limit: number) => ({
    title: 'Guest access',
    body: [
      `Let visitors use your channel without signing in. Guests can browse unlocked media and interact with your Sidekick up to ${limit} times. After ${limit} interactions, or when accessing locked content, guests will be prompted to sign up.`,
      'If user trials are enabled, they can start a free trial. Otherwise, they can subscribe through your external link or request full access from you.',
    ],
    linkWhenOff: 'Turn on guest access →',
    linkWhenOn: 'Guest access is on · change it →',
  }),
}

interface CardHeadingProps {
  icon: Icon
  iconVariant: 'default' | 'filled'
  label: string
  title: string
}

function CardHeading({ icon: HeadingIcon, iconVariant, label, title }: CardHeadingProps) {
  return (
    <Group gap="sm" wrap="nowrap">
      <ThemeIcon variant={iconVariant} size="lg" radius="md">
        <HeadingIcon size={18} />
      </ThemeIcon>
      <div>
        <Text size="xs" c="dimmed" fw={600}>
          {label}
        </Text>
        <Text size="lg" fw={700} lh={1.2}>
          {title}
        </Text>
      </div>
    </Group>
  )
}

interface FeatureCopy {
  title: string
  body: string[]
  linkWhenOn: string
  linkWhenOff: string
}

interface FeatureCardProps {
  icon: Icon
  iconColor: string
  enabled: boolean | undefined
  text: FeatureCopy
  linkTo: string
}

function FeatureCard({ icon: FeatureIcon, iconColor, enabled, text, linkTo }: FeatureCardProps) {
  return (
    <Paper component="section" aria-label={text.title} withBorder radius="md" p="lg">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color={iconColor} size="lg" radius="md">
          <FeatureIcon size={18} />
        </ThemeIcon>
        <Stack gap="xs" style={{ flex: 1 }}>
          <Group justify="space-between" wrap="nowrap" align="center">
            <Group gap="xs" wrap="nowrap">
              <Text size="lg" fw={700} lh={1.2}>
                {text.title}
              </Text>
              {enabled != null && (
                <Badge variant="light" color={enabled ? 'green' : 'gray'} tt="none">
                  {enabled ? 'On' : 'Off'}
                </Badge>
              )}
            </Group>
            <Text size="sm" fw={600} ta="right">
              {copy.noCharge}
            </Text>
          </Group>
          {text.body.map(paragraph => (
            <Text key={paragraph} size="sm" c="dimmed">
              {paragraph}
            </Text>
          ))}
          {/* Until the setting is known, either wording could be wrong. */}
          {enabled != null && (
            <Anchor component={Link} to={linkTo} size="sm" fw={600}>
              {enabled ? text.linkWhenOn : text.linkWhenOff}
            </Anchor>
          )}
        </Stack>
      </Group>
    </Paper>
  )
}

function PriceUnavailable({ title, description }: { title: string; description: string }) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <IconAlertTriangle size={18} color="var(--mantine-color-orange-6)" style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <Text size="sm" fw={600}>
          {title}
        </Text>
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      </div>
    </Group>
  )
}

export function PricingTab() {
  const { api } = useGlobalState()

  const plansQuery = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.getPlans(),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const sponsorInfoQuery = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
  })

  // isPending, not isLoading: a query paused while offline is not fetching, and falling
  // through with no data would claim there is no price.
  if (plansQuery.isPending) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    )
  }

  // isLoadingError, not isError: a failed refetch still has a good price cached.
  const seatPrice = plansQuery.isLoadingError ? null : getSeatPrice(plansQuery.data ?? [])

  const sponsorInfo = sponsorInfoQuery.data
  const accessPath = getRoutePath('access')

  return (
    <Card shadow="sm" p="xl" radius="md" withBorder>
      <Stack gap="lg" maw={880} w="100%" mx="auto" py="md">
        <Text size="xs" fw={700} tt="uppercase" c="var(--mantine-primary-color-filled)" ta="center">
          Pricing
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Paper
            withBorder
            radius="md"
            p="lg"
            bg="gray.0"
            style={{ borderStyle: 'dotted', borderColor: 'var(--mantine-primary-color-filled)' }}>
            <Stack gap="md">
              <CardHeading
                icon={IconGift}
                iconVariant="default"
                label={copy.firstMonth.label}
                title={copy.firstMonth.title}
              />
              <Text size="sm" c="dimmed">
                {copy.firstMonth.body}
              </Text>
              <Divider />
              <Text size="xs" c="dimmed">
                {copy.firstMonth.note}
              </Text>
            </Stack>
          </Paper>

          <Paper withBorder radius="md" p="lg">
            <Stack gap="md">
              <CardHeading
                icon={IconUserCheck}
                iconVariant="filled"
                label={copy.paidUsers.label}
                title={copy.paidUsers.title}
              />
              {seatPrice != null ? (
                <Group gap={6} align="baseline">
                  <Text fz={32} fw={700} lh={1}>
                    {formatDollars(seatPrice)}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {copy.paidUsers.unit}
                  </Text>
                </Group>
              ) : plansQuery.isLoadingError ? (
                <PriceUnavailable
                  title="Pricing information unavailable"
                  description="Something went wrong while loading pricing. Please refresh the page, or contact support if the problem continues."
                />
              ) : (
                <PriceUnavailable
                  title="No pricing available"
                  description="Pricing is not available right now. Please contact support if this continues."
                />
              )}
              <Group gap={6} wrap="nowrap" align="flex-start">
                <IconCheck
                  size={16}
                  color="var(--mantine-primary-color-filled)"
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <Text size="sm">
                  <Text span fw={600} inherit>
                    {copy.paidUsers.included}
                  </Text>{' '}
                  <Text span c="dimmed" size="xs">
                    {copy.paidUsers.includedNote}
                  </Text>
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {copy.paidUsers.body}
              </Text>
              <Anchor component={Link} to={getRoutePath('people')} size="sm" fw={600}>
                {copy.paidUsers.link}
              </Anchor>
            </Stack>
          </Paper>
        </SimpleGrid>

        <Text size="xs" fw={700} tt="uppercase" c="dimmed" mt="sm">
          {copy.optionalFeatures}
        </Text>

        <FeatureCard
          icon={IconUserPlus}
          iconColor="gray"
          enabled={sponsorInfo ? getTrialsEnabled(sponsorInfo) : undefined}
          text={copy.userTrials}
          linkTo={accessPath}
        />
        <FeatureCard
          icon={IconEye}
          iconColor="teal"
          enabled={sponsorInfo?.guestUserAccess}
          text={copy.guestAccess(sponsorInfo?.guestMessageLimit ?? DEFAULT_GUEST_MESSAGE_LIMIT)}
          linkTo={accessPath}
        />
      </Stack>
    </Card>
  )
}
